import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { blog, type BlogPost, type BlogCategory } from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { FormField } from '../components/FormField'
import { Input, Textarea, Select } from '../components/Input'
import { ImageUploader } from '../components/ImageUploader'
import { PageLoader } from '../components/Spinner'
import { toFormData } from '../lib/formData'
import { RichTextEditor } from '../components/RichTextEditor'
import { AuthorPicker } from '../components/AuthorPicker'
import { Dialog, DialogContent, DialogClose } from '../components/Dialog'

export function BlogPostEditor() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [content, setContent] = useState('')
  const [coverImage, setCoverImage] = useState('')
  /* Holds a freshly-picked cover image (Blob) when set. `null` means
     "no new image picked" — on submit we keep the existing URL or
     send null if the user explicitly cleared it. */
  const [coverFile, setCoverFile] = useState<Blob | string | null>(null)
  const [coverCleared, setCoverCleared] = useState(false)
  const [authorUserId, setAuthorUserId] = useState<number | null>(null)
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [tags, setTags] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [slugDirty, setSlugDirty] = useState(false)

  // New-category dialog state
  const [newCatOpen, setNewCatOpen] = useState(false)
  const [newCatName, setNewCatName] = useState('')
  const [newCatSlug, setNewCatSlug] = useState('')
  const [newCatSaving, setNewCatSaving] = useState(false)

  useEffect(() => {
    blog
      .listCategories()
      .then((r) => setCategories(r.categories))
      .catch(() => {
        // non-fatal — the editor still works without a category
      })
  }, [])

  useEffect(() => {
    if (isNew) return
    let cancelled = false
    blog
      .detail(Number(id))
      .then((r) => {
        if (cancelled) return
        const p: BlogPost = r.post
        setTitle(p.title)
        setSlug(p.slug)
        setSlugDirty(true) // don't auto-regenerate on edit
        setExcerpt(p.excerpt)
        setContent(p.content)
        setCoverImage(p.coverImage ?? '')
        setAuthorUserId(p.authorUserId ?? null)
        setCategoryId(p.categoryId ?? null)
        setTags(p.tags.join(', '))
        setIsPublished(p.isPublished)
        setMetaTitle(p.metaTitle ?? '')
        setMetaDescription(p.metaDescription ?? '')
      })
      .catch((err) => toast.error('Failed to load post', { description: err.message }))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, isNew])

  function autoSlug(t: string) {
    return t
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 120)
  }

  function onTitleChange(t: string) {
    setTitle(t)
    // Auto-fill slug only while the user hasn't manually edited it
    // (and we're creating a new post).
    if (isNew && !slugDirty && !slug) setSlug(autoSlug(t))
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        title,
        slug: slug || autoSlug(title),
        excerpt,
        content,
        // coverImage: only include when no new file was picked and
        // the user didn't explicitly clear it. The server picks up
        // the uploaded file via the FormData body.
        ...(coverFile instanceof Blob
          ? {}
          : coverCleared
            ? { coverImage: null }
            : { coverImage: coverImage || null }),
        authorUserId: authorUserId ?? null,
        categoryId: categoryId ?? null,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        isPublished,
        publishedAt: isPublished ? new Date().toISOString() : null,
        metaTitle: metaTitle || null,
        metaDescription: metaDescription || null,
      }

      if (coverFile instanceof Blob) {
        const fd = toFormData(payload, { coverImage: coverFile })
        if (isNew) {
          const r = await blog.create(fd)
          toast.success('Post created')
          navigate(`/blog/${r.post.id}`, { replace: true })
        } else {
          await blog.update(Number(id), fd)
          toast.success('Post saved')
        }
      } else {
        if (isNew) {
          const r = await blog.create(payload)
          toast.success('Post created')
          navigate(`/blog/${r.post.id}`, { replace: true })
        } else {
          await blog.update(Number(id), payload)
          toast.success('Post saved')
        }
      }
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  async function createCategory() {
    if (!newCatName.trim()) {
      toast.error('Name is required')
      return
    }
    setNewCatSaving(true)
    try {
      const r = await blog.createCategory({
        name: newCatName.trim(),
        slug: newCatSlug.trim() || undefined,
      })
      const created = r.category
      setCategories((prev) => [...prev, { ...created, postCount: 0 }])
      setCategoryId(created.id)
      setNewCatOpen(false)
      setNewCatName('')
      setNewCatSlug('')
      toast.success('Category created')
    } catch (err) {
      toast.error('Failed to create category', { description: (err as Error).message })
    } finally {
      setNewCatSaving(false)
    }
  }

  if (loading) return <PageLoader />

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-6xl pb-24">
      <Link
        to="/blog"
        className="text-sm text-slatey hover:text-ink inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="size-3.5" /> Back to blog
      </Link>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold text-ink">
          {isNew ? 'New blog post' : 'Edit post'}
        </h1>
        <Button onClick={onSubmit} loading={saving}>
          <Save className="size-4" />
          {isNew ? 'Create post' : 'Save changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-5">
        {/* ─── LEFT: main content ─────────────────────────────── */}
        <div className="space-y-5 min-w-0">
          <Card className="p-5 space-y-4">
            <FormField label="Title" required>
              <Input
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                required
                maxLength={255}
              />
            </FormField>
            <FormField
              label="Slug"
              required
              hint="URL identifier — auto-generated from title if blank"
            >
              <Input
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value)
                  setSlugDirty(true)
                }}
                placeholder="my-post-title"
                required
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-4">
            <FormField label="Cover image" hint="Recommended 1600×900. WebP preferred.">
              <ImageUploader
                value={coverImage || null}
                onChange={(next) => {
                  if (next instanceof Blob) {
                    setCoverFile(next)
                    setCoverCleared(false)
                  } else if (typeof next === 'string') {
                    setCoverImage(next)
                    setCoverFile(null)
                    setCoverCleared(false)
                  } else {
                    setCoverImage('')
                    setCoverFile(null)
                    setCoverCleared(true)
                  }
                }}
                label="Cover image"
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-3">
            <FormField label="Content" required hint="Rich text — toolbar supports formatting, links, and lists.">
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Start writing the post…"
                minHeight={360}
              />
            </FormField>
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Taxonomy</h2>
            <FormField label="Category">
              <div className="flex gap-2">
                <Select
                  value={categoryId == null ? '' : String(categoryId)}
                  onChange={(e) => {
                    const v = e.target.value
                    setCategoryId(v === '' ? null : Number(v))
                  }}
                  className="flex-1"
                >
                  <option value="">— Uncategorized —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setNewCatOpen(true)}
                >
                  <Plus className="size-4" />
                  New
                </Button>
              </div>
            </FormField>
            <FormField label="Tags" hint="Comma-separated. Up to 20 tags.">
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="ayurveda, herbs, digestion"
              />
            </FormField>
          </Card>
        </div>

        {/* ─── RIGHT: sticky sidebar ─────────────────────────── */}
        <aside className="space-y-5 lg:sticky lg:top-6 lg:self-start">
          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Publish</h2>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="size-4 mt-0.5 accent-forest"
              />
              <div>
                <p className="text-sm font-medium text-ink">Published</p>
                <p className="text-xs text-slatey">
                  Visible on the public blog when checked.
                </p>
              </div>
            </label>
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">Author</h2>
            <FormField
              label="Assigned author"
              hint="Pick the user who wrote this post. Their public author profile is used on the blog."
            >
              <AuthorPicker value={authorUserId} onChange={setAuthorUserId} />
            </FormField>
            {authorUserId != null ? (
              <p className="text-xs text-slatey">
                Edit bio, slug, or photo on the{' '}
                <Link
                  to={`/users/${authorUserId}/author`}
                  className="text-forest underline-offset-2 hover:underline"
                  target="_blank"
                >
                  author profile page
                </Link>
                .
              </p>
            ) : null}
          </Card>

          <Card className="p-5 space-y-4">
            <h2 className="font-display text-base font-semibold text-ink">SEO</h2>
            <FormField label="Excerpt" required hint="Also used as the meta description if empty.">
              <Textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                required
                maxLength={500}
                className="min-h-[80px]"
              />
            </FormField>
            <FormField label="Meta title">
              <Input
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                maxLength={255}
                placeholder={title.slice(0, 60) || 'Defaults to post title'}
              />
            </FormField>
            <FormField label="Meta description">
              <Textarea
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                maxLength={500}
                className="min-h-[80px]"
                placeholder={excerpt.slice(0, 160) || 'Defaults to excerpt'}
              />
            </FormField>
          </Card>
        </aside>
      </div>

      {/* Sticky bottom submit bar — mirrors ProductEditorPage */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-white/90 backdrop-blur px-6 py-3 flex items-center justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/blog')}>
          Cancel
        </Button>
        <Button onClick={onSubmit} loading={saving}>
          <Save className="size-4" />
          {isNew ? 'Create post' : 'Save changes'}
        </Button>
      </div>

      {/* Inline "new category" dialog */}
      <Dialog open={newCatOpen} onOpenChange={setNewCatOpen}>
        <DialogContent title="New category" description="Quickly add a category for this post.">
          <div className="space-y-4">
            <FormField label="Name" required>
              <Input
                value={newCatName}
                onChange={(e) => {
                  setNewCatName(e.target.value)
                  if (!newCatSlug) setNewCatSlug(autoSlug(e.target.value))
                }}
                required
              />
            </FormField>
            <FormField
              label="Slug"
              hint="Auto-generated from name if blank. Lowercase letters, digits, hyphens."
            >
              <Input
                value={newCatSlug}
                onChange={(e) => setNewCatSlug(e.target.value)}
                placeholder="category-slug"
              />
            </FormField>
            <div className="flex justify-end gap-2 pt-2">
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={createCategory} loading={newCatSaving}>
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  )
}
